import { ACTION_KEYS } from "@livingsyncs/types";
import type { Resource, Verb, PermissionKey } from "@livingsyncs/types";
import { can } from "../lib/access";
import { useAuth } from "./useAuth";

/**
 * US-050 — the action-key suffixes (e.g. "administer", "close", "transfer"),
 * derived from `ACTION_KEYS` so a misspelled action does not compile.
 */
type ActionSuffix = (typeof ACTION_KEYS)[number] extends `${string}:${infer S}`
  ? S
  : never;

/**
 * US-050 — typed, implication-aware in-page permission gate. Replaces scattered
 * `context.permissions.includes("…")` literals with one shape:
 *
 *   const canPrescribe = useCan("medication", "write");
 *   const canAdminister = useCan("medication", "administer"); // action overload
 *
 * Reads the SAME shared `can` predicate the backend uses (so `<resource>:write`
 * satisfies a `<resource>:read` check via the implication map). Advisory UX only:
 * the server `decide()` remains the authoritative gate.
 *
 * NOT moved to @livingsyncs/ui (frontend split epic, Phase 2 Task 2.4): its
 * internal useAuth() call would resolve to packages/ui's own useAuth.ts
 * instead of this frontend's `../hooks/useAuth` shim, silently bypassing every
 * test that does `vi.mock("../hooks/useAuth", ...)` to control auth context
 * for a component that calls useCan — confirmed by a real regression (12
 * files / 72 tests failing with "No QueryClient set" once useCan moved).
 * Revisit once frontend's local useAuth/useCan shims are retired (Phase 7),
 * at which point those tests will already be migrated to mock
 * @livingsyncs/ui directly.
 */
export function useCan(resource: Resource, verb: Verb | ActionSuffix): boolean {
  const { data: context } = useAuth();
  const key = `${resource}:${verb}` as PermissionKey;
  return can({ permissions: context?.permissions ?? [] }, key);
}
